import { Injectable } from '@angular/core';

export interface ClockProgram {
  id: number;
  channel: 0 | 1 | 2 | 3;
  type: 0 | 1; // 0 = OFF, 1 = ON
  mode: 1 | 2 | 4 | 8 | 16; // 1=ocaso, 2=orto, 4=ini_reduc, 8=fin_reduc, 16=hora_fija
  daymask: number;
  festive: 0 | 1 | 2 | 3 | 4; // 0=sin festivos, 1-4=periodo activo
  time: number; // en astronómicas = corrección en minutos, puede ser + o -
  timeStr: string;
}

export interface AstroDay {
  sunriseMin: number;
  sunsetMin: number;
  isAllDay: boolean;
  isAllNight: boolean;
  date: string | Date;
  weekday: string;
  sunriseStr: string;
  sunsetStr: string;
}

export interface HolidayPeriod {
  period: 1 | 2 | 3 | 4;
  startDay: number;
  startMonth: number;
  startHour: number;
  startMinute: number;
  startHourStr: string;
  startDayStr: string;
  endDay: number;
  endMonth: number;
  endHour: number;
  endMinute: number;
  endHourStr: string;
  endDayStr: string;
  enable: boolean | 0 | 1;
}

export interface HolidayConfig {
  enable: boolean | 0 | 1;
  periods: HolidayPeriod[];
}

interface ResolvedProgram extends ClockProgram {
  effectiveTime: number;
}

interface TimelineEvent extends ClockProgram {
  effectiveTime: number;   // puede salir de 0..1440 antes del desplazamiento
  absoluteTime: number;    // tiempo absoluto en la línea ampliada
}

interface AbsoluteInterval {
  start: number;
  end: number;
}

export interface RelayInterval {
  startMinutes: number;
  endMinutes: number;
  start: string;
  end: string;
  durationMinutes: number;
  durationHours: number;
}

export interface RelayDayChannelResult {
  channel: 1 | 2;
  intervals: RelayInterval[];
  totalMinutes: number;
  totalHours: number;
}

export interface RelayTodayResult {
  date: string;
  activeFestivePeriod: 0 | 1 | 2 | 3 | 4;
  astro?: AstroDay;
  channels: {
    1: RelayDayChannelResult;
    2: RelayDayChannelResult;
  };
}

export interface RelayRangeChannelTotal {
  channel: 1 | 2;
  totalMinutes: number;
  totalHours: number;
  totalDaysWithOperation: number;
}

export interface RelayRangeTotalsResult {
  startDate: string;
  endDate: string;
  channels: {
    1: RelayRangeChannelTotal;
    2: RelayRangeChannelTotal;
  };
}

@Injectable({
  providedIn: 'root'
})
export class RelayScheduleService {

  getRelayScheduleForToday(
    programs: ClockProgram[],
    astroYear: AstroDay[],
    holidayConfig?: HolidayConfig
  ): RelayTodayResult {
    return this.getRelayScheduleForDate(
      programs,
      astroYear,
      new Date(),
      holidayConfig
    );
  }

  getRelayScheduleForDate(
    programs: ClockProgram[],
    astroYear: AstroDay[],
    targetDate: Date | string,
    holidayConfig?: HolidayConfig
  ): RelayTodayResult {
    const dateObj = this.toLocalDate(targetDate);
    const dateKey = this.toDateKey(dateObj);

    const astroToday = this.findAstroForDate(astroYear, dateKey);
    const activeFestivePeriod = this.getActiveFestivePeriod(dateObj, holidayConfig);

    const emptyResult: RelayTodayResult = {
      date: dateKey,
      activeFestivePeriod,
      astro: astroToday,
      channels: {
        1: {
          channel: 1,
          intervals: [],
          totalMinutes: 0,
          totalHours: 0
        },
        2: {
          channel: 2,
          intervals: [],
          totalMinutes: 0,
          totalHours: 0
        }
      }
    };

    if (!astroToday) {
      return emptyResult;
    }

    return {
      date: dateKey,
      activeFestivePeriod,
      astro: astroToday,
      channels: {
        1: this.calculateChannelScheduleForDate(programs, astroYear, dateObj, 1, holidayConfig),
        2: this.calculateChannelScheduleForDate(programs, astroYear, dateObj, 2, holidayConfig)
      }
    };
  }

  getRelayOperatingTotalsForRange(
    programs: ClockProgram[],
    astroYear: AstroDay[],
    startDate: Date | string,
    endDate: Date | string,
    holidayConfig?: HolidayConfig
  ): RelayRangeTotalsResult {
    const start = this.toLocalDate(startDate);
    const end = this.toLocalDate(endDate);

    const result: RelayRangeTotalsResult = {
      startDate: this.toDateKey(start),
      endDate: this.toDateKey(end),
      channels: {
        1: {
          channel: 1,
          totalMinutes: 0,
          totalHours: 0,
          totalDaysWithOperation: 0
        },
        2: {
          channel: 2,
          totalMinutes: 0,
          totalHours: 0,
          totalDaysWithOperation: 0
        }
      }
    };

    const current = new Date(start);

    while (current <= end) {
      const dayResult = this.getRelayScheduleForDate(
        programs,
        astroYear,
        current,
        holidayConfig
      );

      result.channels[1].totalMinutes += dayResult.channels[1].totalMinutes;
      result.channels[2].totalMinutes += dayResult.channels[2].totalMinutes;

      if (dayResult.channels[1].totalMinutes > 0) {
        result.channels[1].totalDaysWithOperation += 1;
      }

      if (dayResult.channels[2].totalMinutes > 0) {
        result.channels[2].totalDaysWithOperation += 1;
      }

      current.setDate(current.getDate() + 1);
    }

    result.channels[1].totalHours = this.roundTo2(result.channels[1].totalMinutes / 60);
    result.channels[2].totalHours = this.roundTo2(result.channels[2].totalMinutes / 60);

    return result;
  }

  private calculateChannelScheduleForDate(
    programs: ClockProgram[],
    astroYear: AstroDay[],
    targetDate: Date,
    requestedChannel: 1 | 2,
    holidayConfig?: HolidayConfig
  ): RelayDayChannelResult {
    const timelineEvents = this.buildTimelineEventsForDate(
      programs,
      astroYear,
      targetDate,
      requestedChannel,
      holidayConfig
    );

    const absoluteIntervals = this.buildAbsoluteIntervals(timelineEvents);
    const clippedIntervals = this.clipIntervalsToDay(absoluteIntervals, 0, 1440);

    const intervals = clippedIntervals.map(interval =>
      this.buildInterval(interval.start, interval.end)
    );

    const totalMinutes = intervals.reduce((acc, item) => acc + item.durationMinutes, 0);

    return {
      channel: requestedChannel,
      intervals,
      totalMinutes,
      totalHours: this.roundTo2(totalMinutes / 60)
    };
  }

  private buildTimelineEventsForDate(
    programs: ClockProgram[],
    astroYear: AstroDay[],
    targetDate: Date,
    requestedChannel: 1 | 2,
    holidayConfig?: HolidayConfig
  ): TimelineEvent[] {
    const dayOffsets = [-1, 0, 1];
    const timelineEvents: TimelineEvent[] = [];

    for (const dayOffset of dayOffsets) {
      const currentDate = new Date(targetDate);
      currentDate.setDate(currentDate.getDate() + dayOffset);

      const astro = this.findAstroForDate(astroYear, this.toDateKey(currentDate));
      if (!astro) {
        continue;
      }

      const dayOfWeek = this.getDayMaskIndex(currentDate);
      const festivePeriod = this.getActiveFestivePeriod(currentDate, holidayConfig);

      const resolvedPrograms = programs
        .filter(p => this.appliesToRelayChannel(p.channel, requestedChannel))
        .filter(p => this.appliesToDay(p.daymask, dayOfWeek))
        .filter(p => this.matchesFestivePeriod(p, festivePeriod))
        .map(p => this.resolveProgramTime(p, astro));

      const normalizedPrograms = this.normalizeReducedConflicts(
        resolvedPrograms,
        astro.sunriseMin
      );

      const dayPrograms = normalizedPrograms.map((p): TimelineEvent => ({
        ...p,
        absoluteTime: p.effectiveTime + (dayOffset * 1440)
    }));

      timelineEvents.push(...dayPrograms);
    }

    timelineEvents.sort((a, b) => {
      if (a.absoluteTime !== b.absoluteTime) {
        return a.absoluteTime - b.absoluteTime;
      }

      return a.type - b.type; // OFF antes que ON
    });

    return timelineEvents;
  }

  private buildAbsoluteIntervals(events: TimelineEvent[]): AbsoluteInterval[] {
    const intervals: AbsoluteInterval[] = [];

    let isOn = false;
    let currentStart: number | null = null;

    for (const event of events) {
      if (event.type === 1) {
        if (!isOn) {
          isOn = true;
          currentStart = event.absoluteTime;
        }
      } else {
        if (isOn && currentStart !== null && event.absoluteTime > currentStart) {
          intervals.push({
            start: currentStart,
            end: event.absoluteTime
          });
          isOn = false;
          currentStart = null;
        }
      }
    }

    // No cerramos al infinito. Para cálculo diario basta con eventos de -1,0,+1 días.
    // Si quedara ON al final, no añadimos más allá del último evento disponible.
    return intervals;
  }

  private clipIntervalsToDay(
    intervals: AbsoluteInterval[],
    dayStart: number,
    dayEnd: number
  ): AbsoluteInterval[] {
    return intervals
      .map(interval => ({
        start: Math.max(interval.start, dayStart),
        end: Math.min(interval.end, dayEnd)
      }))
      .filter(interval => interval.end > interval.start);
  }

  private getActiveFestivePeriod(
    today: Date,
    holidayConfig?: HolidayConfig
  ): 0 | 1 | 2 | 3 | 4 {
    if (!this.isEnabled(holidayConfig?.enable)) {
      return 0;
    }

    const activePeriod = holidayConfig?.periods
      ?.filter(period => this.isEnabled(period.enable))
      .find(period => this.isDateInsideHolidayPeriod(today, period));

    return activePeriod?.period ?? 0;
  }

  private isDateInsideHolidayPeriod(date: Date, period: HolidayPeriod): boolean {
    const year = date.getFullYear();

    const start = new Date(
      year,
      period.startMonth - 1,
      period.startDay,
      period.startHour,
      period.startMinute,
      0,
      0
    );

    const end = new Date(
      year,
      period.endMonth - 1,
      period.endDay,
      period.endHour,
      period.endMinute,
      0,
      0
    );

    const current = date.getTime();
    const startMs = start.getTime();
    const endMs = end.getTime();

    if (startMs <= endMs) {
      return current >= startMs && current <= endMs;
    }

    // Cruza fin de año
    return current >= startMs || current <= endMs;
  }

  private findAstroForDate(astroYear: AstroDay[], targetDateKey: string): AstroDay | undefined {
    return astroYear.find(item => this.normalizeAstroDate(item.date) === targetDateKey);
  }

private resolveProgramTime(program: ClockProgram, astro: AstroDay): ResolvedProgram {
  let effectiveTime = program.time;

  switch (program.mode) {
    case 1: // ocaso + corrección
      effectiveTime = astro.sunsetMin + program.time;
      break;

    case 2: // orto + corrección
      effectiveTime = astro.sunriseMin + program.time;
      break;

    case 4: // ini_reduc = hora fija
      effectiveTime = program.time;
      break;

    case 8: // fin_reduc = hora fija
      effectiveTime = program.time;
      break;

    case 16: // hora fija
    default:
      effectiveTime = program.time;
      break;
  }

  return {
    ...program,
    effectiveTime
  };
}

  /**
   * 0=lunes ... 6=domingo
   */
  private getDayMaskIndex(date: Date): number {
    const jsDay = date.getDay(); // 0 domingo ... 6 sábado
    return jsDay === 0 ? 6 : jsDay - 1;
  }

  private appliesToDay(daymask: number, dayOfWeek: number): boolean {
    return (daymask & (1 << dayOfWeek)) !== 0;
  }

  private appliesToRelayChannel(programChannel: number, requestedChannel: number): boolean {
    return (programChannel & requestedChannel) !== 0;
  }

  private matchesFestivePeriod(
    program: ClockProgram,
    activeFestivePeriod: 0 | 1 | 2 | 3 | 4
  ): boolean {
    return program.festive === activeFestivePeriod;
  }

  private buildInterval(startMinutes: number, endMinutes: number): RelayInterval {
    const durationMinutes = Math.max(0, endMinutes - startMinutes);

    return {
      startMinutes,
      endMinutes,
      start: this.minutesToHHmm(startMinutes),
      end: this.minutesToHHmm(endMinutes),
      durationMinutes,
      durationHours: this.roundTo2(durationMinutes / 60)
    };
  }

  private toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private normalizeAstroDate(dateValue: string | Date): string {
    if (dateValue instanceof Date) {
      const year = dateValue.getFullYear();
      const month = String(dateValue.getMonth() + 1).padStart(2, '0');
      const day = String(dateValue.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    }

    return dateValue.slice(0, 10);
  }

  private toLocalDate(value: Date | string): Date {
    if (value instanceof Date) {
      return new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        12,
        0,
        0,
        0
      );
    }

    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }

  private isEnabled(value: boolean | number | undefined): boolean {
    return value === true || value === 1;
  }

  private minutesToHHmm(totalMinutes: number): string {
    if (totalMinutes <= 0) {
      return '00:00';
    }

    if (totalMinutes >= 1440) {
      return '24:00';
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private roundTo2(value: number): number {
    return Math.round(value * 100) / 100;
  }


private normalizeReducedConflicts(
  programs: ResolvedProgram[],
  sunriseMinute: number = 0
): ResolvedProgram[] {
  let normalized = [...programs];

  const iniReducOff = normalized
    .filter(p => p.mode === 4 && p.type === 0)
    .sort((a, b) => a.effectiveTime - b.effectiveTime);

  const sunsetOn = normalized
    .filter(p => p.mode === 1 && p.type === 1)
    .sort((a, b) => a.effectiveTime - b.effectiveTime);

  // INI REDUC tiene prioridad sobre OCASO solo en el tramo diurno (orto → ocaso).
  // Horas nocturnas tempranas (00:00, 01:00…) no bloquean el ocaso del mismo día.
  if (iniReducOff.length && sunsetOn.length) {
    normalized = normalized.filter(program => {
      if (!(program.mode === 1 && program.type === 1)) {
        return true;
      }

      return !iniReducOff.some(
        off => off.effectiveTime >= sunriseMinute && off.effectiveTime <= program.effectiveTime
      );
    });
  }

  const finReducOn = normalized
    .filter(p => p.mode === 8 && p.type === 1)
    .sort((a, b) => a.effectiveTime - b.effectiveTime);

  const sunriseOff = normalized
    .filter(p => p.mode === 2 && p.type === 0)
    .sort((a, b) => a.effectiveTime - b.effectiveTime);

  // ORTO tiene prioridad sobre FIN REDUC en el tramo orto → fin reduc.
  if (finReducOn.length && sunriseOff.length) {
    normalized = normalized.filter(program => {
      if (!(program.mode === 8 && program.type === 1)) {
        return true;
      }

      return !sunriseOff.some(
        off => off.effectiveTime >= sunriseMinute && off.effectiveTime <= program.effectiveTime
      );
    });
  }

  return normalized;
}

}
